import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  SettingsState,
  IndirectStaffingFunction,
  UpdateIndirectStaffingConfigRequest,
  UpdateIndirectStaffingConfigResponse,
  FlowPathPercentageConfig,
} from '../../pages/settings/types';
import {
  getIndirectStaffingConfig,
  updateIndirectStaffingConfig as updateIndirectStaffingConfigApi,
  getFlowPathPercentageConfig,
} from '../../pages/settings/api';
// Initial state
const initialState: SettingsState = {
  indirectHC: {
    rules: [],
    loading: false,
    error: null,
    saving: false,
    saveError: null,
  },
  flowPathPercent: {
    configs: [],
    loading: false,
    error: null,
    saving: false,
    saveError: null,
  },
};

// Async thunks
export const fetchIndirectStaffingConfig = createAsyncThunk<
  IndirectStaffingFunction[],
  string,
  { rejectValue: string }
>(
  'settings/fetchIndirectStaffingConfig',
  async (warehouseId: string, { rejectWithValue }) => {
    try {
      const response = await getIndirectStaffingConfig(warehouseId);
      return response.indirectStaffingRules;
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to fetch indirect staffing config';
      return rejectWithValue(message);
    }
  }
);

export const updateIndirectStaffingConfig = createAsyncThunk<
  UpdateIndirectStaffingConfigResponse,
  UpdateIndirectStaffingConfigRequest,
  { rejectValue: string }
>(
  'settings/updateIndirectStaffingConfig',
  async (request: UpdateIndirectStaffingConfigRequest, { rejectWithValue }) => {
    try {
      const response = await updateIndirectStaffingConfigApi(request);
      return response;
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to update indirect staffing config';
      return rejectWithValue(message);
    }
  }
);

export const fetchFlowPathPercentageConfig = createAsyncThunk<
  FlowPathPercentageConfig[],
  string,
  { rejectValue: string }
>(
  'settings/fetchFlowPathPercentageConfig',
  async (warehouseId: string, { rejectWithValue }) => {
    try {
      const response = await getFlowPathPercentageConfig(warehouseId);
      return response.flowPathEntries || [];
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to fetch flow path percentage config';
      return rejectWithValue(message);
    }
  }
);

// Create the slice
const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // fetchIndirectStaffingConfig
    builder
      .addCase(fetchIndirectStaffingConfig.pending, (state) => {
        state.indirectHC.loading = true;
        state.indirectHC.error = null;
      })
      .addCase(fetchIndirectStaffingConfig.fulfilled, (state, action) => {
        state.indirectHC.loading = false;
        state.indirectHC.rules = action.payload;
      })
      .addCase(fetchIndirectStaffingConfig.rejected, (state, action) => {
        state.indirectHC.loading = false;
        state.indirectHC.error = action.payload ?? 'Unknown error';
        state.indirectHC.rules = [];
      });

    // updateIndirectStaffingConfig
    builder
      .addCase(updateIndirectStaffingConfig.pending, (state) => {
        state.indirectHC.saving = true;
        state.indirectHC.saveError = null;
      })
      .addCase(updateIndirectStaffingConfig.fulfilled, (state) => {
        state.indirectHC.saving = false;
      })
      .addCase(updateIndirectStaffingConfig.rejected, (state, action) => {
        state.indirectHC.saving = false;
        state.indirectHC.saveError = action.payload ?? 'Unknown error';
      });

    // fetchFlowPathPercentageConfig
    builder
      .addCase(fetchFlowPathPercentageConfig.pending, (state) => {
        state.flowPathPercent.loading = true;
        state.flowPathPercent.error = null;
      })
      .addCase(fetchFlowPathPercentageConfig.fulfilled, (state, action) => {
        state.flowPathPercent.loading = false;
        state.flowPathPercent.configs = action.payload;
      })
      .addCase(fetchFlowPathPercentageConfig.rejected, (state, action) => {
        state.flowPathPercent.loading = false;
        state.flowPathPercent.error = action.payload ?? 'Unknown error';
        state.flowPathPercent.configs = [];
      });
  },
});

// Export reducer
export default settingsSlice.reducer;
